import type { Component } from "solid-js";
import { createMemo, Show, untrack } from "solid-js";
import EmptyState from "../../../components/emptyState/EmptyState";
import { useTranslation } from "../../../state/i18n";
import Table, { TableActiveFilters, TableBody, TablePagination } from "./Table";
import { condition } from "../../../utils/solidUtils";
import type CustomerId from "../../../state/CustomerId";
import type FormId from "../../../state/FormId";
import ApiService from "../../../services/ApiService";
import { compact, isEmpty, isNil } from "lodash-es";
import type { Task, TaskStatus } from "../../../types/Task";
import FormatDate from "../../../components/formatting/FormatDate";
import Pill from "../../../components/pill/Pill";
import { defaultStringFilter } from "./filter";
import { type IconButtonMenuProps, SplitButtonMenu } from "../../../components/listItemMenu/ListItemMenu";
import { TextButton } from "../../../components/button/Button";
import { useModalManager } from "../../../components/modal/ModalManager";
import GenericFailureModal from "../../GenericFailureModal";
import { ViewTaskModal } from "./ViewTaskModal";
import { getReassignableUsers, STATUS_MAP } from "../../../utils/taskUtil";
import TaskRecipientsCellContent from "./TaskRecipientsCellContent";
import { DestructiveModal, WarningModal } from "../../../components/modal/Modal";
import { keyFromSelector } from "i18next";
import PermissionService from "../../../services/PermissionService.ts";
import DeleteTaskConfirmationModal from "./DeleteTaskConfirmationModal.tsx";
import DeleteTaskExistingSubmissionsModal from "./DeleteTaskExistingSubmissionsModal.tsx";
import ReassignTaskModal from "./ReassignTaskModal";
import isFeatureEnabled from "../../../utils/featureFlag";
import FeatureFlag from "../../../types/FeatureFlag";

type TaskTableProps = {
  customerId: CustomerId;
  formId: FormId;
  formName: string;
};

export const statusFilterOptions = [
  { id: "IN_PROGRESS", label: keyFromSelector(($) => $.TASKS_TABLE_FILTER_STATUS_IN_PROGRESS) },
  { id: "COMPLETED", label: keyFromSelector(($) => $.TASKS_TABLE_FILTER_STATUS_COMPLETED) },
  { id: "REVOKED", label: keyFromSelector(($) => $.TASKS_TABLE_FILTER_STATUS_REVOKED) },
  { id: "DECLINED", label: keyFromSelector(($) => $.TASKS_TABLE_FILTER_STATUS_DECLINED) },
];

const TasksTableView: Component<TaskTableProps> = (props) => {
  const { t } = useTranslation();

  return (
    <Table<Task>
      actionButton={(context) => (
        <TaskRowActionButton task={context.row.original} customerId={props.customerId} formId={props.formId} />
      )}
      columns={[
        {
          id: "status",
          accessorKey: "status",
          enableSorting: false,
          enableColumnFilter: true,
          meta: {
            name: t(($) => $.TASKS_TABLE_COLUMNS_STATUS_HEADER),
            filter: {
              type: "string",
              definition: [
                { operator: "is", inputType: "select", options: statusFilterOptions },
                { operator: "isNot", inputType: "select", options: statusFilterOptions },
              ],
            },
          },
          header: () => <span>{t(($) => $.TASKS_TABLE_COLUMNS_STATUS_HEADER)}</span>,
          cell: (context) => {
            const { label, variant } = STATUS_MAP[context.getValue<TaskStatus>()];
            return <Pill label={t(label)} variant={variant} />;
          },
        },
        {
          id: "dates.creationDate",
          accessorKey: "dates.creationDate",
          enableSorting: true,
          enableColumnFilter: true,
          cell: (context) => <FormatDate value={context.getValue<Date>()} />,
          meta: {
            name: t(($) => $.TASKS_TABLE_COLUMNS_CREATED_ON_HEADER),
            filter: {
              type: "date",
              definition: [
                { operator: "is", inputType: "datetime" },
                { operator: "isBefore", inputType: "datetime" },
                { operator: "isAfter", inputType: "datetime" },
              ],
            },
          },
        },
        {
          id: "dates.publishDate",
          accessorKey: "dates.publishDate",
          enableSorting: true,
          enableColumnFilter: true,
          cell: (context) => <FormatDate value={context.getValue<Date>()} />,
          meta: {
            name: t(($) => $.TASKS_TABLE_COLUMNS_SCHEDULED_ON_HEADER),
            filter: {
              type: "date",
              definition: [
                { operator: "is", inputType: "datetime" },
                { operator: "isBefore", inputType: "datetime" },
                { operator: "isAfter", inputType: "datetime" },
              ],
            },
          },
        },
        {
          id: "dates.informationDate",
          accessorKey: "dates.informationDate",
          enableSorting: true,
          enableColumnFilter: true,
          cell: (context) => (
            <Show when={condition(context.getValue<Date | undefined>(), (date) => !isNil(date))}>
              {(date) => <FormatDate value={date()} />}
            </Show>
          ),
          meta: {
            name: t(($) => $.TASKS_TABLE_COLUMNS_DUE_DATE_HEADER),
            filter: {
              type: "date",
              definition: [
                { operator: "is", inputType: "datetime" },
                { operator: "isBefore", inputType: "datetime" },
                { operator: "isAfter", inputType: "datetime" },
              ],
            },
          },
        },
        {
          id: "message",
          accessorKey: "message",
          enableSorting: true,
          enableColumnFilter: true,
          meta: {
            name: t(($) => $.TASKS_TABLE_COLUMNS_MESSAGE_HEADER),
            filter: {
              type: "string",
              definition: defaultStringFilter,
            },
          },
        },
        {
          id: "users",
          accessorKey: "users",
          enableSorting: false,
          enableColumnFilter: false,
          cell: (context) => <TaskRecipientsCellContent task={context.row.original} />,
          minSize: 300,
          meta: {
            name: t(($) => $.TASKS_TABLE_COLUMNS_SENT_TO_HEADER),
            filter: {
              type: "string",
              definition: defaultStringFilter,
            },
          },
        },
      ]}
      defaultVisibleColumns={[
        "status",
        "dates.creationDate",
        "dates.publishDate",
        "dates.informationDate",
        "message",
        "users",
      ]}
      defaultSorting={[{ id: "dates.creationDate", desc: true }]}
      fetchData={(options) => ApiService.getTasks(props.formId, options)}
      bulkSelect={false}
      emptyState={<NoTasks />}
    >
      <TableActiveFilters />
      <div class="mt-7 flex min-h-0 flex-col">
        <TableBody
          emptyFilteredText={keyFromSelector(($) => $.TASKS_TABLE_EMPTY_FILTERED_DESCRIPTION)}
          loadingText={keyFromSelector(($) => $.TASKS_TABLE_LOADING_DESCRIPTION)}
        />
      </div>
      <TablePagination pageSizeLabel={keyFromSelector(($) => $.TASKS_TABLE_PAGE_SIZE_LABEL)} />
    </Table>
  );
};

const NoTasks = () => {
  const { t } = useTranslation();

  return (
    <EmptyState
      title={t(($) => $.TASKS_TABLE_EMPTY_NONE_TITLE)}
      description={t(($) => $.TASKS_TABLE_EMPTY_NONE_DESCRIPTION)}
      illustration={{ primaryIcon: "document", secondaryIcon: "search" }}
    />
  );
};

type TaskRowActionButtonProps = {
  task: Task;
  customerId: CustomerId;
  formId: FormId;
};

const TaskRowActionButton = (props: TaskRowActionButtonProps) => {
  const { t } = useTranslation();
  const { open, replace } = useModalManager();
  const completeTask = ApiService.completeTask();
  const revokeTask = ApiService.revokeTask();

  const actions = createMemo<IconButtonMenuProps<string>["items"]>(() => [
    compact([
      props.task.status === "IN_PROGRESS" &&
        PermissionService.canUpdateFormTasks(props.customerId, props.formId) && {
          value: "complete",
          type: "action",
          label: t(($) => $.TASKS_TABLE_ACTIONS_COMPLETE),
          variant: "icon",
          icon: { name: "check", type: "outline", class: "icon-secondary" },
          onClick: () => {
            open({
              content: () => (
                <WarningModal
                  title={t(($) => $.TASKS_TABLE_ACTIONS_MODAL_ACTIONS_COMPLETE_TITLE)}
                  description={t(($) => $.TASKS_TABLE_ACTIONS_MODAL_ACTIONS_COMPLETE_DESCRIPTION)}
                  confirmLabel={t(($) => $.TASKS_TABLE_ACTIONS_MODAL_ACTIONS_COMPLETE)}
                  cancelLabel={t(($) => $.TASKS_TABLE_ACTIONS_MODAL_ACTIONS_CANCEL)}
                  onConfirm={() =>
                    completeTask.mutate(
                      untrack(() => ({ formId: props.formId, taskId: props.task.id })),
                      {
                        onError: (err) => {
                          replace({
                            content: () => (
                              <GenericFailureModal
                                title={t(($) => $.TASKS_TABLE_ACTIONS_COMPLETE_FAILURE_MODAL_TITLE)}
                                error={err}
                              />
                            ),
                          });
                        },
                      },
                    )
                  }
                />
              ),
            });
          },
        },
      props.task.status === "IN_PROGRESS" &&
        PermissionService.canDeleteFormTasks(props.customerId, props.formId) && {
          value: "revoke",
          type: "action",
          label: t(($) => $.TASKS_TABLE_ACTIONS_REVOKE),
          variant: "icon",
          icon: { name: "x", type: "outline", class: "icon-secondary" },
          onClick: () => {
            open({
              content: () => (
                <DestructiveModal
                  title={t(($) => $.TASKS_TABLE_ACTIONS_MODAL_ACTIONS_REVOKE_TITLE)}
                  description={t(($) => $.TASKS_TABLE_ACTIONS_MODAL_ACTIONS_REVOKE_DESCRIPTION)}
                  confirmLabel={t(($) => $.TASKS_TABLE_ACTIONS_MODAL_ACTIONS_REVOKE)}
                  cancelLabel={t(($) => $.TASKS_TABLE_ACTIONS_MODAL_ACTIONS_CANCEL)}
                  onConfirm={() =>
                    revokeTask.mutate(
                      untrack(() => ({ formId: props.formId, taskId: props.task.id })),
                      {
                        onError: (err) => {
                          replace({
                            content: () => (
                              <GenericFailureModal
                                title={t(($) => $.TASKS_TABLE_ACTIONS_REVOKE_FAILURE_MODAL_TITLE)}
                                error={err}
                              />
                            ),
                          });
                        },
                      },
                    )
                  }
                />
              ),
            });
          },
        },
      props.task.status === "IN_PROGRESS" &&
        PermissionService.canUpdateFormTasks(props.customerId, props.formId) &&
        isFeatureEnabled(FeatureFlag.TASK_REASSIGN) &&
        !isEmpty(getReassignableUsers(props.task)) && {
          value: "reassign",
          type: "action",
          label: t(($) => $.TASKS_TABLE_ACTIONS_REASSIGN),
          variant: "icon",
          icon: { name: "reassign", type: "outline", class: "icon-secondary" },
          onClick: () => {
            open({
              content: () => <ReassignTaskModal task={props.task} />,
            });
          },
        },
      PermissionService.canDeleteFormTasks(props.customerId, props.formId) && {
        value: "delete",
        type: "action",
        label: t(($) => $.TASKS_TABLE_ACTIONS_DELETE),
        variant: "icon",
        icon: { name: "trash", type: "outline", class: "icon-secondary" },
        onClick: () => {
          const hasSubmissions = props.task.fulfilments.some(({ registrationId }) => !isNil(registrationId));

          open({
            content: () =>
              hasSubmissions ? (
                <DeleteTaskExistingSubmissionsModal />
              ) : (
                <DeleteTaskConfirmationModal formId={props.formId} taskId={props.task.id} />
              ),
          });
        },
      },
    ]),
  ]);

  return (
    <Show
      when={!isEmpty(actions().flat())}
      fallback={
        <TextButton
          variant="secondary"
          size="medium"
          onClick={() => {
            open({
              content: () => <ViewTaskModal task={props.task} />,
            });
          }}
          text={t(($) => $.TASKS_TABLE_ACTIONS_VIEW)}
        />
      }
    >
      <SplitButtonMenu
        label={t(($) => $.TASKS_TABLE_ACTIONS_VIEW)}
        actionButtonProps={{
          onClick: () => {
            open({
              content: () => <ViewTaskModal task={props.task} />,
            });
          },
        }}
        menuButtonProps={{
          "aria-label": t(($) => $.ACTIONS_ARIA),
        }}
        loading={false}
        items={actions()}
        status="none"
      />
    </Show>
  );
};

export default TasksTableView;
